// Coils Printing Station — operator kiosk for the coils section (Raspberry Pi +
// display, like the packing station). Lists pallets ready to finalize; pressing
// "PRINT & DONE" prints a pallet label (coils + QR) on the Zebra and marks the
// pallet done (print first → then complete, so a print failure never completes).
// Shares the dark kiosk theme (.labelstation-root) with the L120 station.
import { useState, useEffect, useCallback } from 'react';
import './labelStation.css';
import { LabelCanvas, buildRotatedPalletZpl, logoFinal, ceMarking } from './labelKit.js';
import { API } from '../../config/api';

const ENG_BASE = API.BACKEND;

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.getItem('token') || ''
  };
}

async function engReq(path, options = {}) {
  const res = await fetch(`${ENG_BASE}${path}`, { headers: authHeaders(), ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
  }
  return data;
}

function OperatorSelect({ value, onChange }) {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    engReq('/api/v1/operators')
      .then(res => setOperators((res?.data || []).filter(op => op.active)))
      .catch(err => console.error('Failed to load operators:', err))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="operator-select">
      <select value={value} onChange={e => onChange(e.target.value)} disabled={loading}>
        <option value="">{loading ? 'Loading...' : 'Select Operator'}</option>
        {operators.map(op => (
          <option key={op._id} value={op.name}>
            {op.name} (Shift {op.shift})
          </option>
        ))}
      </select>
    </div>
  );
}

function coilLine(item) {
  const qty = Number(item.coilCount) || 1;
  const len = Number(item.coilLength) || Number(item.assignedLength) || 0;
  return `${qty} × ${Number(len).toLocaleString()}m`;
}

// Pallet label — LANDSCAPE 150 × 100 mm (10 × 15 cm read along the length). Authored
// in reading orientation (1200×800); printed rotated 90° onto the 100mm portrait media.
// Framed, logo+CE header, coil table (multiple products), big QR (ENG:PALLET:<code>).
const PALLET_W = 1200, PALLET_H = 800;

// The words on a pallet label, per market. An export pallet crosses a border
// wearing this; "PALET COLACI" and "Scaneaza la receptie" mean nothing at the
// far end. Only prose is translated — the pallet code, the order code and the
// address are identifiers.
const PALLET_WORDS = {
  ro: {
    title: 'PALET COLACI', client: 'Client', order: 'Comanda',
    product: 'Produs', colour: 'Culoare', coils: 'Colaci x m', kg: 'kg',
    more: n => `... +${n} mai multe produse`,
    total: (c, m, k) => `TOTAL: ${c} colaci · ${m} m · ${k} kg`,
    scan: 'Scaneaza la receptie', date: 'Data', operator: 'Operator', lot: 'LOT'
  },
  en: {
    title: 'COIL PALLET', client: 'Customer', order: 'Order',
    product: 'Product', colour: 'Colour', coils: 'Coils x m', kg: 'kg',
    more: n => `... +${n} more products`,
    total: (c, m, k) => `TOTAL: ${c} coils · ${m} m · ${k} kg`,
    scan: 'Scan at reception', date: 'Date', operator: 'Operator', lot: 'BATCH'
  }
};


// Roughly how wide a bold sans string renders, per character per point. It only
// has to be generous enough that a row never overlaps its own numbers, and 0.58
// leaves room on every product name in the catalogue.
// Both renderers draw in monospace — the preview as a DOM div with
// `fontFamily: monospace`, the print through a canvas with the same font — so
// one character really is a fixed fraction of the type size. 0.6 is the advance
// width of the standard monospace stack.
const MONO_W = 0.6;
const widthOf = (text, size) => String(text || '').length * size * MONO_W;

// Space-grouped, not `toLocaleString`: Romanian formats thousands with a dot,
// and "10.000 m" read at five metres is indistinguishable from ten.
const grp = v => String(Math.round(Number(v) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

function palletLabelSpec(p, { operator = '' } = {}) {
  const els = [];
  // `market` and `clientNote` are resolved by the backend, which can reach the
  // order; the station only has the pallet. Anything that is not "export" is
  // internal, so a missing field cannot produce an English label by accident.
  const w = PALLET_WORDS[p.market === 'export' ? 'en' : 'ro'];
  const items = Array.isArray(p.items) ? p.items : [];
  const code = p.code || '-';
  const year = new Date().getFullYear();
  const date = new Date().toLocaleDateString('ro-RO');
  const totalCoils = Number(p.totalCoils) || items.reduce((s, i) => s + (Number(i.coilCount) || 0), 0);

  const PANEL_X = 26;
  const RIGHT = 1174;

  els.push({ type: 'box', x: 8, y: 8, w: 1184, h: 784, t: 3 });

  // ── header, compressed ──
  // The title used to take 168px of a 800px label to say the same word on every
  // one of them. The pallet CODE is what distinguishes this label, so it is the
  // big thing now and the title is a caption.
  els.push({ type: 'logo', x: 20, y: 14, src: logoFinal, w: 150, h: 92 });
  els.push({ type: 'logo', x: 1104, y: 14, src: ceMarking, w: 70, h: 48 });
  els.push({ type: 'text', x: 190, y: 18, w: 880, align: 'center', size: 30, bold: true, text: w.title });
  els.push({ type: 'text', x: 190, y: 56, w: 880, align: 'center', size: 46, bold: true, text: code });
  els.push({ type: 'line', x: 8, y: 116, w: 1184, h: 3 });

  // ── who it is for ──
  // Two halves that cannot reach each other, rather than two full-width lines
  // that happened not to collide with the names we tried.
  els.push({
    type: 'text', x: PANEL_X, y: 128, w: 640, size: 34, bold: true,
    text: `${w.client}: ${p.customerName || 'STOC'}`
  });
  els.push({
    type: 'text', x: 690, y: 134, w: RIGHT - 690, align: 'right', size: 28,
    text: `${w.order}: ${p.orderCode || '-'}`
  });
  els.push({ type: 'line', x: 16, y: 176, w: 1168, h: 1 });

  // ── the products get the FULL width ──
  //
  // The QR sat in a right-hand column and took 314px off this panel, which cost
  // the product line a third of its type size. It is at the foot now, beside the
  // totals, where it steals height nobody was using instead of width everybody
  // was.
  const PANEL_W = RIGHT - PANEL_X;
  const shownItems = items.slice(0, 6);
  const n = Math.max(1, shownItems.length);
  const TOP = 190;
  const BOTTOM = 462;
  const row = Math.floor((BOTTOM - TOP) / n);

  const nameOf = it => {
    const shown = (p.displayNames && p.displayNames[it.jobId]) || null;
    const base = shown
      ? `${shown} ${it.numberOfConductors ?? ''}x${it.section ?? ''}`.trim()
      : it.productLabel || '-';
    // The sheath colour lives on the standard; the item carries it only when
    // somebody put it there. Both are tried before giving up.
    const colour = (p.displayColors && p.displayColors[it.jobId]) || it.color || null;
    return colour ? `${base} · ${String(colour).toUpperCase()}` : base;
  };
  const sumOf = it => {
    const qty = Number(it.coilCount) || 1;
    const len = Number(it.coilLength) || Number(it.assignedLength) || 0;
    return `${qty} x ${grp(len)} m = ${grp(qty * len)} m · ${grp(it.weightKg)} ${w.kg}`;
  };

  // Two columns that cannot overlap, and a size computed rather than guessed:
  // both renderers draw in monospace, so a character really is a fixed fraction
  // of the type size. The split follows the CONTENT so the name and the figures
  // end up the same size — a fixed ratio gave the name 44pt and left the
  // quantity at 25, which is backwards on a label whose point is the quantity.
  const GAP = 24;
  const maxName = Math.max(1, ...shownItems.map(it => nameOf(it).length));
  const maxSum = Math.max(1, ...shownItems.map(it => sumOf(it).length));

  let size = Math.max(13, Math.min(42, row - 8));
  while (size > 13 && (maxName + maxSum) * size * MONO_W + GAP > PANEL_W) size -= 1;

  const NAME_W = Math.min(
    Math.ceil(maxName * size * MONO_W) + 8,
    PANEL_W - GAP - Math.ceil(maxSum * size * MONO_W)
  );
  const SUM_X = PANEL_X + NAME_W + GAP;
  const SUM_W = PANEL_W - NAME_W - GAP;

  // Each line sits centred in its own slot rather than at the top of it: with
  // one product the band is tall and a line pinned to the top leaves the space
  // under it empty.
  const pad = Math.max(0, Math.round((row - size) / 2));
  let ry = TOP + pad;
  shownItems.forEach((it, i) => {
    if (i > 0) els.push({ type: 'line', x: 16, y: ry - pad - 2, w: 1168, h: 1 });
    els.push({ type: 'text', x: PANEL_X, y: ry, w: NAME_W, size, bold: true, text: nameOf(it) });
    els.push({
      type: 'text', x: SUM_X, y: ry, w: SUM_W, align: 'right', size, bold: true, text: sumOf(it)
    });
    ry += row;
  });

  if (items.length > shownItems.length) {
    els.push({
      type: 'text', x: PANEL_X, y: BOTTOM - 22, size: 20,
      text: w.more(items.length - shownItems.length)
    });
  }

  // ── the QR, sized to be scanned from a forklift ──
  //
  // `^BQ` magnification tops out at 10 in ZPL, which is a 1.25 mm module at
  // 203 dpi — and the MODULE size, not the overall square, is what decides the
  // distance a scanner reads from. So 10 it is, and the box is drawn to match
  // what that actually prints (~25-29 modules for this payload, 31-36 mm).
  // Larger would mean rasterising the code instead of emitting a native ^BQ,
  // which costs its rotation-invariance.
  const QR_S = 290;
  const QR_X = RIGHT - QR_S;
  const QR_Y = 476;
  els.push({ type: 'qr', x: QR_X, y: QR_Y, s: QR_S, mag: 10, payload: `ENG:PALLET:${code}` });
  els.push({
    type: 'text', x: QR_X, y: QR_Y + QR_S + 4, w: QR_S, align: 'center', size: 19, bold: true,
    text: w.scan
  });

  // ── totals band, left of the QR ──
  //
  // The count leads, at a size meant to be read from a forklift: it is the one
  // number the person taking the pallet has to agree with.
  const LEFT_W = QR_X - 16 - 20;
  els.push({ type: 'box', x: 16, y: 476, w: LEFT_W, h: 96, t: 3 });
  els.push({ type: 'text', x: 34, y: 488, size: 70, bold: true, text: grp(totalCoils) });
  els.push({
    type: 'text', x: 34 + widthOf(grp(totalCoils), 70) + 18, y: 528,
    size: 34, bold: true, text: w.coils.split(' ')[0].toUpperCase()
  });
  els.push({
    type: 'text', x: 360, y: 508, w: LEFT_W - 360 + 16 - 18, align: 'right', size: 40, bold: true,
    text: `${grp(p.totalLengthM)} m · ${grp(p.totalWeightKg)} ${w.kg}`
  });

  // ── foot, left of the QR ──
  // Printed verbatim, where a receiving warehouse looks for it.
  if (p.clientNote) {
    els.push({
      type: 'text', x: PANEL_X, y: 590, w: LEFT_W - 10, size: 32, bold: true,
      text: String(p.clientNote).slice(0, 32)
    });
  }
  const metaY = p.clientNote ? 646 : 600;
  els.push({ type: 'text', x: PANEL_X, y: metaY, size: 24, text: `${w.date}: ${date}` });
  els.push({ type: 'text', x: PANEL_X, y: metaY + 34, size: 24, text: `${w.operator}: ${operator || '-'}` });
  els.push({ type: 'text', x: PANEL_X, y: metaY + 68, size: 24, text: `${w.lot}: ${code} / ${year}` });
  els.push({
    type: 'text', x: PANEL_X, y: 762, w: LEFT_W - 10, size: 17,
    text: 'Energoplast · 420005 Bistrita, Agronomului H 12'
  });

  return els;
}

function PalletCard({ pallet, operator, onFinalized, images }) {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(false);

  const handlePrintDone = async () => {
    if (busy) return;
    if (!operator) {
      setFeedback({ type: 'error', msg: 'Select an operator first' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      // 1) print FIRST — build the ZPL from the SAME preview spec, so what prints
      //    matches the preview. Printer failure → 502 → we do NOT complete the pallet.
      const zpl = buildRotatedPalletZpl(
        palletLabelSpec(pallet, { operator }),
        { labelW: PALLET_W, labelH: PALLET_H, images }
      );
      await engReq(`/api/v1/packing/pallets/${pallet.code}/print`, {
        method: 'POST',
        body: JSON.stringify({ operator, zpl })
      });
      // 2) then mark done (creates the warehouse StorageItem)
      await engReq(`/api/v1/packing/pallets/${pallet.code}/done`, {
        method: 'POST',
        body: JSON.stringify({ operator })
      });
      setFeedback({ type: 'success', msg: 'Printed + done' });
      onFinalized?.();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message });
    } finally {
      setBusy(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const items = Array.isArray(pallet.items) ? pallet.items : [];

  return (
    <div className="pallet-card">
      <div className="pallet-head">
        <div>
          <span className="pallet-code">{pallet.code}</span>
          <span className="pallet-client">{pallet.customerName || 'Stock'}</span>
          {pallet.orderCode && <span className="pallet-order">{pallet.orderCode}</span>}
        </div>
        <div className="pallet-totals">
          <span>{Number(pallet.totalLengthM || 0).toLocaleString()} m</span>
          <span>{Number(pallet.totalWeightKg || 0).toFixed(1)} kg</span>
        </div>
      </div>

      <div className="pallet-items">
        {items.map((it, i) => (
          <div key={it.tokenId || i} className="pallet-item">
            <span className="pi-qty">{coilLine(it)}</span>
            <span className="pi-name">{it.productLabel}</span>
            {it.color && (
              <span className="qi-color" data-color={String(it.color).toLowerCase()}>
                {it.color}
              </span>
            )}
          </div>
        ))}
        {!items.length && <span className="queue-empty">No coils on this pallet</span>}
      </div>

      <div className="pallet-actions">
        <button className="btn-preview" onClick={() => setPreview(true)}>PREVIEW</button>
        <button className="btn-print" onClick={handlePrintDone} disabled={busy}>
          {busy ? 'PRINTING...' : 'PRINT & DONE'}
        </button>
      </div>
      {feedback && <div className={`feedback ${feedback.type}`}>{feedback.msg}</div>}

      {preview && (
        <div className="lbl-modal-overlay" onClick={() => setPreview(false)}>
          <div className="lbl-modal" onClick={e => e.stopPropagation()}>
            <div className="lbl-modal-head">
              <span>Preview etichetă palet — {pallet.code}</span>
              <button onClick={() => setPreview(false)}>✕</button>
            </div>
            <LabelCanvas
              elements={palletLabelSpec(pallet, { operator })}
              labelW={PALLET_W}
              labelH={PALLET_H}
              widthPx={700}
            />
            <div className="lbl-note">15 × 10 cm (landscape) · printed rotated 90° · QR = ENG:PALLET:{pallet.code}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CoilsPrintingStation() {
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operator, setOperator] = useState('');
  const [images, setImages] = useState({});

  // preload logo + CE as Image objects (drawn onto the rotated print canvas)
  useEffect(() => {
    const map = {};
    let n = 0;
    const done = () => { if (++n === 2) setImages({ ...map }); };
    const load = src => { const img = new Image(); img.onload = () => { map[src] = img; done(); }; img.onerror = done; img.src = src; };
    load(logoFinal); load(ceMarking);
  }, []);

  const fetchPallets = useCallback(async () => {
    try {
      setError(null);
      const res = await engReq('/api/v1/packing/pallets');
      const list = (res?.data || []).filter(p => p.status !== 'done');
      setPallets(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPallets();
    const interval = setInterval(fetchPallets, 15000);
    return () => clearInterval(interval);
  }, [fetchPallets]);

  return (
    <div className="labelstation-root">
      <div className="app">
        <header className="app-header">
          <div className="header-left">
            <h1>Coils Printing Station</h1>
            <span className="machine-status running">{pallets.length} pallets</span>
          </div>
          <OperatorSelect value={operator} onChange={setOperator} />
        </header>

        {error && <div className="error-bar">API Error: {error}</div>}

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <main className="app-main">
            {pallets.length === 0 ? (
              <div className="production-info empty">
                <span className="no-production">No pallets to finalize</span>
              </div>
            ) : (
              pallets.map(p => (
                <PalletCard
                  key={p.code}
                  pallet={p}
                  operator={operator}
                  onFinalized={fetchPallets}
                  images={images}
                />
              ))
            )}
          </main>
        )}
      </div>
    </div>
  );
}
